import React, { useCallback } from "react"

import {
  CopyableId,
  DrawerBody,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Skeleton,
} from "@coval/ui"

import { DrawerOpenInNewTabAction } from "@/components/drawer/DrawerOpenInNewTabAction"
import { ReadOnlyRegionProvider } from "@/components/version-history/ReadOnlyRegion"
import type { AgentRecord } from "@/types/api/agent"

import { useAgentTransport } from "../transport"
import { AgentDetailsForm } from "./AgentDetailsForm"
import { AgentForm } from "./AgentForm"

interface AgentDrawerPanelProps {
  agentId: string
}

function AgentDrawerContent({ agent }: { agent: AgentRecord }) {
  const formData = {
    display_name: agent.display_name,
    model_type: agent.model_type ?? "",
    phone_number: agent.phone_number ?? "",
    endpoint: agent.endpoint ?? "",
    metadata: (agent.metadata ?? {}) as Record<string, unknown>,
    attributes: (agent.attributes ?? {}) as Record<string, unknown>,
    prompt: agent.workflows?.prompt ?? "",
    workflows: agent.workflows ?? null,
    metric_ids: [],
    apply_linked_metrics_to_conversation_uploads: false,
  }
  const noop = useCallback(() => {}, [])

  return (
    <ReadOnlyRegionProvider readOnly>
      <div className="space-y-8">
        <AgentForm
          formData={formData}
          onFormDataChange={noop}
          currentAgent={agent}
          sectioned
        />
        <AgentDetailsForm formData={formData} onFormDataChange={noop} />
      </div>
    </ReadOnlyRegionProvider>
  )
}

/**
 * One agent's read-only detail view, as the body of a drawer the caller owns.
 * The caller supplies the drawer so a single panel can swap between resources
 * instead of stacking one drawer per kind.
 */
export const AgentDrawerPanel: React.FC<AgentDrawerPanelProps> = ({
  agentId,
}) => {
  const { data: agent, isLoading } = useAgentTransport(agentId)

  return (
    <>
      <DrawerHeader
        actions={
          <DrawerOpenInNewTabAction
            path={`/agents/${agentId}`}
            label="Open agent in new tab"
          />
        }
      >
        <DrawerTitle>
          {isLoading ? "Loading…" : (agent?.display_name ?? "Agent")}
        </DrawerTitle>
        {agent ? (
          <DrawerDescription className="flex items-center gap-1">
            <span>ID:</span>
            <CopyableId id={agent.id} label="Copy agent ID" />
          </DrawerDescription>
        ) : null}
      </DrawerHeader>
      <DrawerBody>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : agent ? (
          <AgentDrawerContent agent={agent} />
        ) : (
          <p className="text-coval-text-muted text-sm">Agent not found.</p>
        )}
      </DrawerBody>
    </>
  )
}
